// Onboarding em etapas (wizard de criação). Substitui o tour guiado de tooltips
// e o checklist. Deixa o perfil pronto (foto + @ + bio), ativa e TESTA as
// notificações (com confirmação de recebimento + retry + pular) e ensina o
// básico — terminando com "adicionar o primeiro livro".
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from './icons';
import { api } from '../services/apiV2';
import { CoverCropper } from './clubs/cover-cropper';
import { isNativeApp, registerNativePush } from './platform';
import { MiniProfilePreview, MiniBellPreview, MiniBookPreview } from './slide-tour';

type Props = {
  open: boolean;
  session: any;
  apiBase: string;
  profile: any;
  onAddBook: () => void;
  onFinish: (patch?: any) => void; // marca onboarding visto + fecha
  /** Já tem livro na estante? Então o fim não manda "adicionar o primeiro". */
  temLivro?: boolean;
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px', borderRadius: 12,
  background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink)',
  fontSize: 15, outline: 'none',
};
const labelStyle: React.CSSProperties = {
  fontFamily: 'Geist Mono, monospace', fontSize: 10.5, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: 'var(--ink-3)',
};

export const OnboardingWizard: React.FC<Props> = ({ open, session, apiBase, profile, onAddBook, onFinish, temLivro = false }) => {
  const native = isNativeApp();
  // passos dinâmicos: notificações só no app nativo
  const steps = ['welcome', 'profile', ...(native ? ['notif'] : []), 'howto', 'done'];
  const [si, setSi] = useState(0);
  const step = steps[Math.min(si, steps.length - 1)];

  // ---- perfil ----
  const [name, setName] = useState(profile?.display_name || '');
  const [username, setUsername] = useState((profile?.username || '').toLowerCase());
  const [bio, setBio] = useState(profile?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);
  const [avatarPicked, setAvatarPicked] = useState<any>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileErr, setProfileErr] = useState<string | null>(null);
  const avatarInputRef = useRef<any>(null);

  // O wizard fica montado desde o boot, quando o perfil ainda nem chegou: os
  // campos nasciam vazios. Ao abrir, completa com o que a pessoa já tem — sem
  // passar por cima do que ela já digitou (pente fino, QA-09).
  useEffect(() => {
    if (!open || !profile) return;
    setName((v: string) => v || profile.display_name || '');
    setUsername((v: string) => v || (profile.username || '').toLowerCase());
    setBio((v: string) => v || profile.bio || '');
    setAvatarUrl((v: any) => v || profile.avatar_url || null);
  }, [open, profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAvatarFile = (e: any) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) setAvatarPicked(f); };
  const confirmAvatar = async (cf: any) => {
    setAvatarPicked(null); setUploadingAvatar(true); setProfileErr(null);
    try { const r = await api.uploadAvatar(session, cf); setAvatarUrl(r.url); }
    catch (e: any) { setProfileErr(e?.message || 'Erro ao enviar foto'); }
    finally { setUploadingAvatar(false); }
  };
  const saveProfile = async (): Promise<boolean> => {
    setSavingProfile(true); setProfileErr(null);
    const patch: any = {};
    if (name.trim()) patch.display_name = name.trim();
    patch.bio = bio;
    const u = username.trim().toLowerCase();
    if (u && u !== (profile?.username || '').toLowerCase()) patch.username = u;
    try {
      const up = await api.patchProfile(session, patch);
      try { window.dispatchEvent(new CustomEvent('forjamente:profile-updated', { detail: { ...up, avatar_url: avatarUrl } })); } catch {}
      setSavingProfile(false);
      return true;
    } catch (e: any) { setProfileErr(e?.message || 'Não consegui salvar. Tente outro @ ou pule.'); setSavingProfile(false); return false; }
  };

  // ---- notificações ----
  // idle | requesting | sent | received | failed | denied | skipped
  const [notif, setNotif] = useState<string>('idle');
  const gotRef = useRef(false);
  const recvRef = useRef<any>(null);
  const timerRef = useRef<any>(null);
  const dbg = useCallback((data: any) => {
    try { fetch(`${apiBase}/debug-log`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tag: 'onboarding-notif', data }) }).catch(() => {}); } catch {}
  }, [apiBase]);

  const clearNotifWatch = useCallback(() => {
    if (recvRef.current) { window.removeEventListener('forjamente:push-received', recvRef.current); recvRef.current = null; }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  const runNotifTest = useCallback(async () => {
    clearNotifWatch();            // não empilha listener/timer em retries
    gotRef.current = false;
    setNotif('requesting');
    const r = await registerNativePush(apiBase, session.accessToken).catch(() => ({ ok: false, error: 'erro' } as any));
    if (!r.ok) { setNotif(r.error === 'permission_denied' ? 'denied' : 'failed'); dbg({ phase: 'register', ok: false, error: r.error }); return; }
    const onRecv = () => { gotRef.current = true; setNotif('received'); dbg({ phase: 'received', ok: true }); clearNotifWatch(); };
    recvRef.current = onRecv;
    window.addEventListener('forjamente:push-received', onRecv);
    setNotif('sent');
    await api.testPush(session).catch(() => {});
    timerRef.current = setTimeout(() => {
      if (!gotRef.current) { clearNotifWatch(); setNotif('failed'); dbg({ phase: 'timeout', ok: false }); }
    }, 12000);
  }, [apiBase, session, dbg, clearNotifWatch]);

  useEffect(() => { return () => { gotRef.current = true; clearNotifWatch(); }; }, [clearNotifWatch]);

  if (!open) return null;

  const go = (d: number) => setSi(v => Math.max(0, Math.min(steps.length - 1, v + d)));
  const last = si >= steps.length - 1;

  const next = async () => {
    if (step === 'profile') { const ok = await saveProfile(); if (!ok) return; }
    if (last) { onFinish(); if (!temLivro) onAddBook(); return; }
    go(1);
  };

  // rótulo do botão principal por passo
  const primaryLabel = step === 'done' ? (temLivro ? 'Concluir' : 'Adicionar primeiro livro') : (step === 'profile' && savingProfile ? 'Salvando…' : 'Próximo');

  // validação inline do @ (servidor exige 3–30 e ao menos 1 alfanumérico)
  const uTrim = username.trim();
  const usernameInvalid = uTrim.length > 0 && (uTrim.length < 3 || !/[a-z0-9]/.test(uTrim));
  const profileBlocked = step === 'profile' && (savingProfile || usernameInvalid);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'var(--paper)', display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto', animation: 'e2FadeUp 0.25s ease both' }}>
      {/* progresso */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'calc(16px + env(safe-area-inset-top,0px)) 18px 8px' }}>
        <div style={{ flex: 1, display: 'flex', gap: 5 }}>
          {steps.map((_, k) => (<div key={k} style={{ flex: 1, height: 3.5, borderRadius: 999, background: k <= si ? 'var(--ember)' : 'var(--paper-3)', transition: 'background 0.3s' }} />))}
        </div>
        {step !== 'done' && step !== 'notif' && <button onClick={() => { onFinish(); }} style={{ fontSize: 13.5, color: 'var(--ink-3)', fontWeight: 600, padding: '4px 6px', background: 'none', border: 'none' }}>Pular</button>}
      </div>

      {/* corpo */}
      <div key={step} style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 24px', display: 'flex', flexDirection: 'column', justifyContent: step === 'profile' ? 'flex-start' : 'center', animation: 'e2FadeUp 0.3s ease both' }}>

        {step === 'welcome' && (<>
          <div style={{ marginBottom: 26 }}><MiniProfilePreview nome={profile?.display_name} usuario={profile?.username} /></div>
          <div className="mono" style={labelStyle}>Bem-vindo</div>
          <h2 style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 30, lineHeight: 1.1, color: 'var(--ink)', margin: '8px 0 14px' }}>Vamos deixar tudo pronto</h2>
          <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0 }}>Em 1 minuto seu perfil fica pronto, você ativa os lembretes e já aprende a registrar suas leituras. Bora?</p>
        </>)}

        {step === 'profile' && (<>
          <div className="mono" style={labelStyle}>Passo 1 · seu perfil</div>
          <h2 style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 27, lineHeight: 1.1, color: 'var(--ink)', margin: '8px 0 18px' }}>Monte seu perfil</h2>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <button onClick={() => avatarInputRef.current && avatarInputRef.current.click()} disabled={uploadingAvatar} style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <div style={{ width: 92, height: 92, borderRadius: '50%', overflow: 'hidden', background: 'var(--paper-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--line)' }}>
                {avatarUrl ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon name="user" size={40} color="var(--ink-3)" />}
              </div>
              <div style={{ position: 'absolute', right: -2, bottom: -2, width: 32, height: 32, borderRadius: '50%', background: 'var(--ember)', color: '#fff', display: 'grid', placeItems: 'center', border: '2px solid var(--paper)' }}><Icon name="camera" size={16} color="#fff" /></div>
              {uploadingAvatar && <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,.55)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 12 }}>enviando…</div>}
              <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onAvatarFile} />
            </button>
          </div>
          <label style={labelStyle}>Nome</label>
          <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder="Seu nome" style={{ ...inputStyle, marginTop: 7, marginBottom: 16 }} />
          <label style={labelStyle}>Nome de usuário</label>
          <div style={{ position: 'relative', marginTop: 7, marginBottom: 6 }}>
            <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', fontFamily: 'Geist Mono, monospace', fontSize: 15 }}>@</span>
            <input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, '').slice(0, 30))} placeholder="usuario" style={{ ...inputStyle, paddingLeft: 28, fontFamily: 'Geist Mono, monospace' }} />
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: usernameInvalid ? 4 : 16 }}>Único no app — é seu link <span style={{ color: 'var(--ember)' }}>forjamente.com.br/@{username || 'usuario'}</span></div>
          {usernameInvalid && <div style={{ fontSize: 11.5, color: '#E2574C', marginBottom: 16 }}>{uTrim.length < 3 ? 'Pelo menos 3 caracteres.' : 'Use ao menos uma letra ou número.'}</div>}
          <label style={labelStyle}>Bio</label>
          <textarea value={bio} onChange={(e) => setBio(e.target.value.slice(0, 200))} placeholder="Conte do que você gosta de ler…" rows={3} style={{ ...inputStyle, marginTop: 7, resize: 'none', lineHeight: 1.45 }} />
          {profileErr && <div style={{ fontSize: 12.5, color: '#E2574C', marginTop: 10 }}>{profileErr}</div>}
        </>)}

        {step === 'notif' && (<>
          <div style={{ marginBottom: 24 }}><MiniBellPreview /></div>
          <div className="mono" style={labelStyle}>Passo 2 · lembretes</div>
          <h2 style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 27, lineHeight: 1.1, color: 'var(--ink)', margin: '8px 0 12px' }}>Ative os lembretes</h2>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ink-2)', margin: '0 0 18px' }}>
            {notif === 'received' ? 'Notificação recebida! Tudo certo — os lembretes vão te ajudar a manter a chama acesa. 🔥'
              : notif === 'denied' ? 'A permissão foi negada. Você pode ativar depois em Ajustes do iPhone → Notificações → Forjamente, ou pular por agora.'
              : notif === 'failed' ? 'Não consegui confirmar o recebimento. Vamos tentar de novo? (No iPhone o 1º registro pode demorar alguns segundos.)'
              : notif === 'sent' ? 'Enviando uma notificação de teste… aguarde alguns segundos.'
              : notif === 'requesting' ? 'Pedindo permissão…'
              : 'Receba um empurrãozinho diário pra não perder o hábito. Vou pedir a permissão e mandar um teste pra confirmar que chega certinho.'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 12, background: notif === 'received' ? 'rgba(199,83,26,0.10)' : 'var(--paper-2)', border: `1px solid ${notif === 'received' ? 'var(--ember)' : 'var(--line)'}` }}>
            {notif === 'received'
              ? <><Icon name="check" size={18} color="var(--ember)" stroke={2.5} /><span style={{ fontSize: 14, color: 'var(--ember)', fontWeight: 600 }}>Recebido com sucesso</span></>
              : (notif === 'requesting' || notif === 'sent')
                ? <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>⏳ {notif === 'requesting' ? 'Aguardando permissão…' : 'Esperando o teste chegar…'}</span>
                : <span style={{ fontSize: 14, color: 'var(--ink-3)' }}>Lembretes ainda não ativados</span>}
          </div>
        </>)}

        {step === 'howto' && (<>
          <div style={{ marginBottom: 24 }}><MiniBookPreview /></div>
          <div className="mono" style={labelStyle}>Como funciona</div>
          <h2 style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 27, lineHeight: 1.1, color: 'var(--ink)', margin: '8px 0 16px' }}>O dia a dia no app</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              ['flame', 'Registre sua leitura', 'Toque no + e marque quantas páginas leu. Cada dia acende a chama (sua sequência).'],
              ['book', 'Defina sua meta', 'Quantas páginas por dia? Você escolhe — o anel do dia mostra o quanto falta.'],
              ['users', 'Leia em comunidade', 'Entre num clube, siga leitores e veja todo mundo avançando junto na Home.'],
            ].map(([icon, t, d], k) => (
              <div key={k} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--paper-2)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={icon as any} size={18} color="var(--ember)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{t}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.45 }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
        </>)}

        {step === 'done' && (<>
          <div style={{ fontSize: 56, textAlign: 'center', marginBottom: 20 }}>📖</div>
          <div className="mono" style={labelStyle}>Tudo pronto</div>
          <h2 style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 30, lineHeight: 1.1, color: 'var(--ink)', margin: '8px 0 14px' }}>{temLivro ? 'Tudo certo' : 'Bora começar a ler'}</h2>
          <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink-2)', margin: '0 0 14px' }}>{temLivro ? 'Seu perfil está pronto. O livro já está na estante — é só seguir lendo.' : 'Adicione seu primeiro livro pra acender a primeira chama. Cada página conta.'}</p>
          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 12, background: 'var(--paper-2)', border: '1px solid var(--line)' }}>
            <Icon name="sparkle" size={16} color="var(--ember)" />
            <span style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.45 }}>Quer rever este tutorial (ou os de Clube e Explorar)? É só ir em <strong style={{ color: 'var(--ink-2)' }}>Eu → Ajustes → Tutoriais</strong>.</span>
          </div>
          {native && notif !== 'received' && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 12, background: 'var(--paper-2)', border: '1px solid var(--line)', marginTop: 10 }}>
              <Icon name="bell" size={16} color="var(--ember)" />
              <span style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.45 }}>Pulou os lembretes? Pode ativar quando quiser em <strong style={{ color: 'var(--ink-2)' }}>Eu → Hábito de notificações</strong>.</span>
            </div>
          )}
        </>)}
      </div>

      {/* rodapé */}
      <div style={{ padding: '12px 18px calc(20px + env(safe-area-inset-bottom,0px))', borderTop: '1px solid var(--line)' }}>
        {step === 'notif' ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {notif === 'received'
              ? <button onClick={() => go(1)} style={primaryBtn}>Próximo</button>
              : (notif === 'idle')
                ? <><button onClick={() => { setNotif('skipped'); go(1); }} style={ghostBtn}>Agora não</button><button onClick={runNotifTest} style={primaryBtn}>Permitir e testar</button></>
                : (notif === 'failed' || notif === 'denied')
                  ? <><button onClick={() => { setNotif('skipped'); go(1); }} style={ghostBtn}>Pular</button><button onClick={runNotifTest} style={primaryBtn}>Tentar de novo</button></>
                  : <><button onClick={() => { clearNotifWatch(); setNotif('skipped'); go(1); }} style={ghostBtn}>Pular</button><button disabled style={{ ...primaryBtn, opacity: 0.7 }}>Aguarde…</button></>}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {si > 0 ? <button onClick={() => go(-1)} style={ghostBtn}>Voltar</button> : <div style={{ width: 1 }} />}
            <button onClick={next} disabled={profileBlocked} style={{ ...primaryBtn, ...(profileBlocked ? { opacity: 0.55 } : {}) }}>
              {primaryLabel}{step !== 'done' && !savingProfile && <Icon name="chevron-right" size={16} color="#fff" />}
            </button>
          </div>
        )}
      </div>

      {avatarPicked && <CoverCropper file={avatarPicked} aspect={1} outW={480} outH={480} title="Ajustar foto" hint="Arraste pra posicionar · sua foto de perfil" onCancel={() => setAvatarPicked(null)} onConfirm={confirmAvatar} />}
    </div>
  );
};

const primaryBtn: React.CSSProperties = {
  flex: 1, padding: '14px', borderRadius: 12, background: 'var(--ember)', color: '#fff',
  fontSize: 15, fontWeight: 700, border: 'none', boxShadow: '0 10px 24px -8px rgba(199,83,26,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  padding: '13px 18px', borderRadius: 12, background: 'var(--paper-2)', border: '1px solid var(--line)',
  color: 'var(--ink-2)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
